import { useEffect, useMemo, useState } from "react";
import PasswordProtection from "@/components/PasswordProtection";
const format = (d: Date, opts: { withYear?: boolean; withWeekday?: boolean } = {}) => {
  const date = d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(opts.withYear ? { year: "numeric" } : {}),
    ...(opts.withWeekday ? { weekday: "short" } : {}),
  });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time}`;
};
import { Star, X } from "lucide-react";

const PASSWORD = "Podspods2901!";
const STORAGE_KEY = "feedbackResponsesAuth";
const FN_URL = "https://pxurtuktvoeaytlozzue.supabase.co/functions/v1/get-feedback-responses";

interface FeedbackRow {
  id: string;
  session_id: string | null;
  email: string;
  use_case: string | null;
  overall_rating: number | null;
  would_book_again: string | null;
  responses: Record<string, any>;
  created_at: string;
  session: {
    id: string;
    pod_id: string | null;
    user_email: string | null;
    start_time: string | null;
    end_time: string | null;
    access_code: number | null;
    pod: { id: string; name: string | null; location: string | null } | null;
  } | null;
}

export default function FeedbackResponses() {
  const [authed, setAuthed] = useState(() => localStorage.getItem(STORAGE_KEY) === "true");

  if (!authed) {
    return (
      <PasswordProtection
        password={PASSWORD}
        onSuccess={() => {
          localStorage.setItem(STORAGE_KEY, "true");
          setAuthed(true);
        }}
      />
    );
  }
  return <Dashboard />;
}

function Dashboard() {
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<FeedbackRow | null>(null);
  const [filterUseCase, setFilterUseCase] = useState<string>("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(FN_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-admin-password": PASSWORD },
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        setRows(data.responses ?? []);
      } catch (e: any) {
        setError("Failed to load responses. Try refreshing.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(
    () => (filterUseCase ? rows.filter((r) => r.use_case === filterUseCase) : rows),
    [rows, filterUseCase],
  );

  const stats = useMemo(() => {
    const total = rows.length;
    const rated = rows.filter((r) => r.overall_rating != null);
    const avg = rated.length ? rated.reduce((s, r) => s + (r.overall_rating ?? 0), 0) / rated.length : 0;
    const wouldBookAgain = rows.filter((r) => r.would_book_again === "yes").length;
    const pctYes = total ? Math.round((wouldBookAgain / total) * 100) : 0;
    return { total, avg, pctYes };
  }, [rows]);

  const useCases = useMemo(() => [...new Set(rows.map((r) => r.use_case).filter(Boolean) as string[])], [rows]);

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground font-sans">loading...</div>;
  }
  if (error) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-red-600 font-sans">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-baseline justify-between mb-8">
          <h1 className="font-funnel text-3xl text-foreground">
            feedback <span className="font-cormorant italic">responses</span>
          </h1>
          <button
            onClick={() => {
              localStorage.removeItem(STORAGE_KEY);
              location.reload();
            }}
            className="text-xs text-muted-foreground hover:text-foreground font-sans"
          >
            sign out
          </button>
        </div>

        <div className="grid grid-cols-2 tablet:grid-cols-4 gap-3 mb-8">
          <StatCard label="total responses" value={stats.total.toString()} />
          <StatCard label="avg rating" value={stats.avg ? stats.avg.toFixed(1) : "—"} />
          <StatCard label="would book again" value={`${stats.pctYes}%`} />
          <StatCard label="with session" value={rows.filter((r) => r.session_id).length.toString()} />
        </div>

        <div className="flex gap-2 flex-wrap mb-4">
          <button
            onClick={() => setFilterUseCase("")}
            className={`px-3 py-1.5 rounded-full text-xs font-sans border ${!filterUseCase ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}
          >
            all
          </button>
          {useCases.map((u) => (
            <button
              key={u}
              onClick={() => setFilterUseCase(u)}
              className={`px-3 py-1.5 rounded-full text-xs font-sans border ${filterUseCase === u ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}
            >
              {u}
            </button>
          ))}
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm font-sans">
            <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">when</th>
                <th className="text-left px-4 py-3">email</th>
                <th className="text-left px-4 py-3">use case</th>
                <th className="text-left px-4 py-3">overall</th>
                <th className="text-left px-4 py-3">again?</th>
                <th className="text-left px-4 py-3">session</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className="border-t border-border hover:bg-muted/30 cursor-pointer"
                >
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {format(new Date(r.created_at))}
                  </td>
                  <td className="px-4 py-3 text-foreground">{r.email}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.use_case ?? "—"}</td>
                  <td className="px-4 py-3">
                    {r.overall_rating ? (
                      <span className="inline-flex items-center gap-1">
                        <Star size={12} className="fill-primary text-primary" />
                        {r.overall_rating}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {r.would_book_again ? (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          r.would_book_again === "yes"
                            ? "bg-primary/10 text-primary"
                            : r.would_book_again === "no"
                              ? "bg-red-100 text-red-700"
                              : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {r.would_book_again}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {r.session ? "linked" : "—"}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                    no responses yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <DetailDrawer row={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="text-xs text-muted-foreground font-sans mb-1">{label}</div>
      <div className="text-2xl font-funnel text-foreground">{value}</div>
    </div>
  );
}

function DetailDrawer({ row, onClose }: { row: FeedbackRow; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative w-full max-w-lg bg-background h-full overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="font-funnel text-xl text-foreground">response</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div>
            <div className="text-xs text-muted-foreground font-sans uppercase mb-1">from</div>
            <div className="text-foreground font-sans">{row.email}</div>
            <div className="text-xs text-muted-foreground font-sans mt-1">
              {format(new Date(row.created_at), { withYear: true, withWeekday: true })}
            </div>
          </div>

          {row.session && (
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="text-xs text-muted-foreground font-sans uppercase mb-2">linked session</div>
              {row.session.pod?.name && (
                <div className="font-sans text-foreground text-sm mb-1">{row.session.pod.name}</div>
              )}
              {row.session.pod?.location && (
                <div className="text-xs text-muted-foreground font-sans mb-2">{row.session.pod.location}</div>
              )}
              {row.session.start_time && (
                <div className="text-xs text-muted-foreground font-sans">
                  started: {format(new Date(row.session.start_time))}
                </div>
              )}
              {row.session.end_time && (
                <div className="text-xs text-muted-foreground font-sans">
                  ended: {format(new Date(row.session.end_time))}
                </div>
              )}
            </div>
          )}

          <div>
            <div className="text-xs text-muted-foreground font-sans uppercase mb-2">answers</div>
            <pre className="bg-card border border-border rounded-xl p-4 text-xs font-mono whitespace-pre-wrap break-words text-foreground">
{JSON.stringify(row.responses, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
